import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import { StickyHeaderNavigation, OTPInput, AppButton } from '@/components'
import { useProfileUpdateStore } from '@/store/profileUpdate'
import {
  useSendPhoneVerificationCode,
  useVerifyPhoneUpdate,
} from '@/domain/identity/useCases'
import { responsiveAdapter, responsiveAdapterVertical } from '@/utils'
import { toast } from 'react-hot-toast'

const CELL_COUNT = 4
const defaultCodes = Array(CELL_COUNT).fill('')

export const VerifyPhoneNumberScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [codes, setCodes] = useState<string[]>(defaultCodes)
  const [codeErrorMessage, setCodeErrorMessage] = useState('')

  // Store state (as in React Native)
  const {
    newPhoneNumber,
    phoneErrorMessage,
    newPhoneMessagingMethodCode,
    setProfileUpdateFormState,
  } = useProfileUpdateStore()

  // API for resending code
  const { mutate: sendPhoneVerificationCode, isPending: isResendingCode } =
    useSendPhoneVerificationCode({
      onError: ({ errorMessage }) => {
        if (errorMessage) {
          toast.error(errorMessage)
        }
      },
    })

  // API for verifying code
  const { mutate: verifyPhoneUpdate, isPending: isVerifying } =
    useVerifyPhoneUpdate({
      onSuccess: () => {
        if (phoneErrorMessage) {
          setProfileUpdateFormState({
            phoneErrorMessage: '',
            phoneIsValid: true,
          })
        }

        // Show success message and navigate back to profile
        toast.success(t('screens:editPhoneNumber.phoneNumberUpdated'))
        navigate('/profile/edit-profile')
      },
      onError: ({ errorMessage, code }) => {
        if (!errorMessage) {
          return
        }

        if (code === 'TOO_MANY_ATTEMPTS' && errorMessage) {
          setCodes(defaultCodes)
          return toast.error(errorMessage)
        }

        if (code === 'CODE_NOT_VERIFIED' || code === 'CODE_INVALID') {
          setCodes(defaultCodes)
          setCodeErrorMessage(errorMessage)
          return
        }

        if (code === 'PHONE_NUMBER_ALREADY_EXISTS_ERROR') {
          setCodes(defaultCodes)
          setCodeErrorMessage(errorMessage)
          return
        }

        setCodeErrorMessage(
          t('api:validationErrors.invalidCode') || 'Invalid code',
        )
        setCodes(defaultCodes)
      },
    })

  // Handle code submission (as in React Native)
  const handleSubmitForm = (fullCode: string) => {
    verifyPhoneUpdate({
      code: fullCode,
      phone: newPhoneNumber || '',
      new_phone: true,
    })
  }

  // Resend code (as in React Native)
  const handleResendCode = () => {
    sendPhoneVerificationCode({
      phone_number: newPhoneNumber || '',
      send_method: newPhoneMessagingMethodCode || 'sms',
    })
  }

  // Handle OTP change
  const handleOTPChange = (newCodes: string[]) => {
    setCodes(newCodes)
    setCodeErrorMessage('') // Clear error on input

    // If all fields filled, submit automatically
    const fullCode = newCodes.join('')
    if (fullCode.length === CELL_COUNT) {
      handleSubmitForm(fullCode)
    }
  }

  const subtitle = `${t('screens:editPhoneNumber.weSentCodeToYourNewPhone')} ${newPhoneNumber}, ${t('screens:editPhoneNumber.enterForVerification')}`

  return (
    <BaseView
      style={{
        flex: 1,
        backgroundColor: '#0A0A0B',
        paddingTop: 16,
      }}
    >
      {/* Header */}
      <StickyHeaderNavigation
        title={t(
          'screens:profile.profileSettings.items.editProfile.updatePhoneNumber',
        )}
      />

      <BaseView
        style={{
          flex: 1,
          paddingHorizontal: responsiveAdapter(20),
          paddingTop: responsiveAdapterVertical(24),
        }}
      >
        {/* Content */}
        <BaseView style={{ gap: responsiveAdapterVertical(24) }}>
          {/* Title and Description */}
          <BaseView style={{ gap: responsiveAdapterVertical(12) }}>
            <BaseText
              fontSize={24}
              fontFamily="semibold"
              color="label.default"
              textAlign="center"
            >
              {t('screens:auth.enterVerificationCode')}
            </BaseText>

            <BaseText
              fontSize={16}
              fontFamily="regular"
              color="label.secondary"
              textAlign="center"
              style={{ lineHeight: '22px' }}
            >
              {subtitle}
            </BaseText>
          </BaseView>

          {/* OTP Input */}
          <BaseView style={{ alignItems: 'center' }}>
            <OTPInput
              length={CELL_COUNT}
              value={codes}
              onChange={handleOTPChange}
              errorMessage={codeErrorMessage}
              disabled={isVerifying}
            />
          </BaseView>

          {/* Resend Code Button */}
          <BaseView style={{ alignItems: 'center', marginTop: 24 }}>
            <AppButton
              type="link"
              label={t('screens:auth.resendCode')}
              onPress={handleResendCode}
              disabled={isResendingCode}
              isLoading={isResendingCode}
            />
          </BaseView>
        </BaseView>

        {/* Footer - can add additional actions if needed */}
        <BaseView style={{ marginTop: 'auto', paddingBottom: 24 }}>
          {/* Empty space for future actions */}
        </BaseView>
      </BaseView>
    </BaseView>
  )
}
